import React, { useState, useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Plus, Play, Pause, CheckCircle, Factory, ClipboardCheck, AlertTriangle,
  Printer, FileText, RotateCcw, Eye, Timer, Package, Link2, Unlink, Hash,
  ShieldCheck, ShieldAlert, ShieldX, Tag, Trash2, Target, Zap,
} from 'lucide-react';
import { PageHeader, KpiCard, STREAMS } from '../components/PageHeader';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import WorkOrderLabelDialog from '../components/WorkOrderLabelDialog';
import ProductionProgressWidget from '../components/ProductionProgressWidget';

const TAB_KIND = {
  finished_production: 'final_product',
  part_assembly: 'part_assembly',
  quality_verification: 'qc',
};

const ProductionQueuePage = ({ level }) => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [boms, setBoms] = useState([]);
  const [families, setFamilies] = useState([]);
  const [queueCategories, setQueueCategories] = useState([]);
  const [finishedUnits, setFinishedUnits] = useState([]);
  const [activeTab, setActiveTab] = useState('part_assembly');
  const [activeCategory, setActiveCategory] = useState('all');

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [wasteDialogOpen, setWasteDialogOpen] = useState(false);
  // iter43 — BOM-by-WO picker for waste flow
  const [bomLines, setBomLines] = useState([]);
  const [linkChildrenOpen, setLinkChildrenOpen] = useState(false);
  const [buildSheetOpen, setBuildSheetOpen] = useState(false);
  const [qcRunOpen, setQcRunOpen] = useState(false);
  const [qcViewOpen, setQcViewOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // iter78q — Print Label dialog target (WO + optional unit)
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelTarget, setLabelTarget] = useState({ wo: null, unit: null });

  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // iter78 — per-WO time-entry breakdown shown inside the Details dialog
  const [woTimeData, setWoTimeData] = useState(null); // { entries, summary } | null
  const [woTimeLoading, setWoTimeLoading] = useState(false);
  const [userNameMap, setUserNameMap] = useState({});

  // Forms
  const [formData, setFormData] = useState({
    bom_id: '', product_type_id: '', target_quantity: 1,
    queue_type: 'part_assembly', queue_category: '', is_final_product: false,
    family_id: '', priority: 5, notes: '', due_date: '',
    auto_release: true, // iter78l — default ON
  });

  const [wasteData, setWasteData] = useState({
    item_sku: '', item_name: '', quantity: 1, waste_type: 'Damaged',
    reason: '', cost_impact: 0,
  });

  // Final product link/build
  const [linkSerialInput, setLinkSerialInput] = useState('');
  const [linkAddedSerials, setLinkAddedSerials] = useState([]);
  const [buildSheet, setBuildSheet] = useState(null);

  // QC
  const [qcTemplates, setQcTemplates] = useState([]);
  const [qcTplId, setQcTplId] = useState('');
  const [qcUnitTarget, setQcUnitTarget] = useState('');
  const [qcAnswers, setQcAnswers] = useState([]);
  const [qcInspectorNotes, setQcInspectorNotes] = useState('');
  const [qcSubmissions, setQcSubmissions] = useState([]);
  const [qcSubView, setQcSubView] = useState(null);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [activeTab, level]);

  useEffect(() => {
    let interval;
    if (activeTimer) {
      interval = setInterval(() => {
        const start = new Date(activeTimer.clock_in).getTime();
        setElapsedTime(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  // iter78 — when the Details dialog opens for a WO, fetch its per-user time breakdown.
  useEffect(() => {
    if (!detailDialogOpen || !selectedWO?.id) {
      setWoTimeData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setWoTimeLoading(true);
      try {
        const [tRes, uRes] = await Promise.all([
          api().get(`/time-entries/work-order/${selectedWO.id}`),
          api().get('/users/assignable').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setWoTimeData(tRes.data || null);
        const map = {};
        (uRes.data || []).forEach((u) => {
          map[u.id] = u.full_name || u.email || u.id;
        });
        setUserNameMap(map);
      } catch (e) {
        if (!cancelled) setWoTimeData(null);
      } finally {
        if (!cancelled) setWoTimeLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailDialogOpen, selectedWO?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [woRes, ptRes, bomsRes, famRes, catRes, fuRes, timerRes, qcTplRes, qcSubRes] = await Promise.all([
        api().get(`/work-orders?queue_type=${activeTab}`),
        api().get('/product-types'),
        api().get('/boms'),
        api().get('/product-families').catch(() => ({ data: [] })),
        api().get('/queue-categories'),
        api().get('/finished-units'),
        api().get('/time-entries/active').catch(() => ({ data: null })),
        api().get('/qc-templates?active=true').catch(() => ({ data: [] })),
        api().get('/qc-submissions').catch(() => ({ data: [] })),
      ]);
      const inLevel = (ptId) => !level || ((ptRes.data.find(p => p.id === ptId) || {}).level || 'product') === level;
      setWorkOrders(woRes.data.filter(w => inLevel(w.product_type_id)));
      setProductTypes(ptRes.data.filter(p => inLevel(p.id)));
      setBoms(bomsRes.data.filter(b => inLevel(b.product_type_id)));
      setFamilies(famRes.data);
      setQueueCategories(catRes.data);
      setFinishedUnits(fuRes.data.filter(u => inLevel(u.product_type_id)));
      setActiveTimer(timerRes.data);
      setQcTemplates(qcTplRes.data);
      setQcSubmissions(qcSubRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const ptMap = useMemo(() => productTypes.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}), [productTypes]);
  const bomMap = useMemo(() => boms.reduce((acc, b) => { acc[b.id] = b; return acc; }, {}), [boms]);
  const fuById = useMemo(() => finishedUnits.reduce((acc, u) => { acc[u.id] = u; return acc; }, {}), [finishedUnits]);
  const fuBySerial = useMemo(() => finishedUnits.reduce((acc, u) => { acc[u.serial_number] = u; return acc; }, {}), [finishedUnits]);

  const handleCreateWO = async () => {
    if (!formData.bom_id) { toast.error('Please select a BOM'); return; }
    const bom = bomMap[formData.bom_id];
    if (bom?.status !== 'Released') {
      toast.error('Only Released BOMs can be used. Release the BOM from the BOM detail page first.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData, product_type_id: bom.product_type_id, target_quantity: 1 };
      if (!payload.family_id) delete payload.family_id;
      if (!payload.due_date) delete payload.due_date;
      const autoRel = payload.auto_release;
      delete payload.auto_release;
      await api().post(`/work-orders?auto_release=${autoRel ? 'true' : 'false'}`, payload);
      toast.success('Production order created');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (woId, newStatus) => {
    try {
      await api().put(`/work-orders/${woId}/status?new_status=${newStatus}`);
      toast.success(`Status: ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  // iter69 — Approval / Reject / Finalize handlers (Phase 4)
  const canApprove = user?.role === 'Admin' || user?.role === 'ProductionSupervisor' || user?.role === 'Production Supervisor';
  // iter77 — operator-notification preference (default ON, persisted in localStorage)
  const [notifyOnApprove, setNotifyOnApprove] = useState(() => {
    try { return localStorage.getItem('wo_notify_on_approve') !== '0'; } catch { return true; }
  });
  const toggleNotifyOnApprove = (v) => {
    setNotifyOnApprove(v);
    try { localStorage.setItem('wo_notify_on_approve', v ? '1' : '0'); } catch (e) { void e; }
  };
  const handleApproveWO = async (wo) => {
    try {
      const r = await api().post(`/work-orders/${wo.id}/approve?notify_operator=${notifyOnApprove ? 'true' : 'false'}`);
      const notified = r.data?.notified;
      if (notifyOnApprove && notified?.in_app && wo.assigned_to) {
        toast.success(`Approved — operator notified${notified.email ? ' (in-app + email)' : ' (in-app)'}`);
      } else if (notifyOnApprove && wo.assigned_to && !notified?.in_app) {
        toast.success('Work order approved (notification skipped)');
      } else {
        toast.success('Work order approved — timers may now start');
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const handleRejectWO = async (wo) => {
    const reason = window.prompt('Reject reason?', '');
    if (reason === null) return;
    try {
      await api().post(`/work-orders/${wo.id}/reject`, { reason });
      toast.success('Work order rejected');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };
  const handleFinalizeWO = async (wo) => {
    if (!window.confirm(`Finalize WO ${wo.work_order_number || wo.id.slice(0, 8)}? Mints unique serials for completed units + attaches latest passing QC + cascades sub-BOM serials.`)) return;
    try {
      const r = await api().post(`/work-orders/${wo.id}/finalize`, {});
      toast.success(r.data.message || 'Finalized');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Finalize failed'); }
  };
  const ApprovalBadge = ({ wo }) => {
    if (wo.approval_status === 'approved') return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs whitespace-nowrap" data-testid={`wo-approved-${wo.id}`} title={`Approved by ${wo.approved_by_name || ''}`}><ShieldCheck className="w-3.5 h-3.5" /> Approved</span>;
    if (wo.approval_status === 'rejected') return <span className="inline-flex items-center gap-1 text-red-700 text-xs whitespace-nowrap" data-testid={`wo-rejected-${wo.id}`} title={wo.rejected_reason || ''}><ShieldX className="w-3.5 h-3.5" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 text-amber-700 text-xs whitespace-nowrap" data-testid={`wo-pending-${wo.id}`}><ShieldAlert className="w-3.5 h-3.5" /> Pending</span>;
  };

  const handleStartTimer = async (wo) => {
    if (activeTimer) { toast.error('Stop the running timer first'); return; }
    try {
      await api().post('/time-entries/clock-in', { work_order_id: wo.id });
      if (wo.status === 'Released') await api().put(`/work-orders/${wo.id}/status?new_status=InProgress`);
      toast.success('Timer started');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handlePauseTimer = async () => {
    if (!activeTimer) return;
    try {
      await api().put(`/time-entries/${activeTimer.id}/clock-out`, { units_completed: 0 });
      await api().put(`/work-orders/${activeTimer.work_order_id}/status?new_status=Paused`);
      toast.success('Paused');
      setActiveTimer(null); setElapsedTime(0);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleCompleteWO = async (wo) => {
    setLoading(true);
    try {
      // iter78p — auto-release Draft/Paused WOs so Complete works in a single click.
      // Surface real errors (e.g. WO not approved) instead of silently swallowing.
      if (wo.status === 'Draft' || wo.status === 'Paused') {
        await api().put(`/work-orders/${wo.id}/status?new_status=Released`);
      }
      if (activeTimer && activeTimer.work_order_id === wo.id) {
        await api().put(`/time-entries/${activeTimer.id}/clock-out`, { units_completed: 1 });
        setActiveTimer(null); setElapsedTime(0);
      }
      const serialRes = await api().post('/serial-configs/default/generate').catch(() => null);
      const serialNumber = serialRes?.data?.serial_number || `SN-${Date.now()}`;
      const unitKind = wo.queue_type === 'finished_production' ? 'final_product' : 'part_assembly';
      await api().post('/finished-units', {
        product_type_id: wo.product_type_id,
        work_order_id: wo.id,
        bom_id: wo.bom_id,
        serial_number: serialNumber,
        unit_kind: unitKind,
        family_id: wo.family_id || null,
      });
      await api().put(`/work-orders/${wo.id}/status?new_status=Completed`);
      toast.success(`Completed! Serial: ${serialNumber}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setLoading(false);
    }
  };

  const handleReopenWO = async (woId) => {
    try {
      await api().put(`/work-orders/${woId}/reopen`);
      toast.success('Reopened');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // iter78r — Delete WO (any status). Cascades minted serials when ?force=true.
  const handleDeleteWO = async (wo) => {
    const woLabel = `WO-${wo.id.slice(0, 8).toUpperCase()}`;
    if (!window.confirm(`Delete ${woLabel}? This is permanent and removes any logged time on it.`)) return;
    try {
      await api().delete(`/work-orders/${wo.id}`);
      toast.success(`${woLabel} deleted`);
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Failed to delete';
      if (status === 409) {
        if (!window.confirm(`${detail}\n\nForce-delete (also wipe minted serials + consumption snapshots)?`)) return;
        try {
          await api().delete(`/work-orders/${wo.id}?force=true`);
          toast.success(`${woLabel} force-deleted`);
          fetchData();
        } catch (e2) { toast.error(e2.response?.data?.detail || 'Force-delete failed'); }
      } else {
        toast.error(detail);
      }
    }
  };

  const handleLogWaste = async () => {
    if (!selectedWO) return;
    try {
      await api().post('/waste-entries', { work_order_id: selectedWO.id, ...wasteData });
      toast.success('Waste logged');
      setWasteDialogOpen(false);
      setWasteData({ item_sku: '', item_name: '', quantity: 1, waste_type: 'Damaged', reason: '', cost_impact: 0 });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const resetForm = () => {
    setFormData({
      bom_id: '', product_type_id: '', target_quantity: 1,
      queue_type: activeTab, queue_category: '',
      is_final_product: activeTab === 'finished_production',
      family_id: '', priority: 5, notes: '', due_date: '',
      auto_release: true,
    });
  };

  const onSelectBom = (bomId) => {
    const bom = bomMap[bomId];
    setFormData(f => ({ ...f, bom_id: bomId, product_type_id: bom?.product_type_id || '' }));
  };

  // ===== Final Product helpers =====
  const finalProductUnitFor = (wo) => finishedUnits.find(u => u.work_order_id === wo.id);

  const openLinkChildren = (wo) => {
    setSelectedWO(wo);
    setSelectedUnit(finalProductUnitFor(wo));
    setLinkSerialInput('');
    setLinkAddedSerials([]);
    setLinkChildrenOpen(true);
  };

  const addSerialToLink = (sn) => {
    const trimmed = (sn || linkSerialInput).trim();
    if (!trimmed) return;
    const child = fuBySerial[trimmed];
    if (!child) { toast.error(`Serial ${trimmed} not found`); return; }
    if (child.unit_kind === 'final_product') { toast.error('Cannot link a final product to another'); return; }
    if (child.parent_finished_unit_id && child.parent_finished_unit_id !== selectedUnit?.id) {
      toast.error(`Serial ${trimmed} is already linked to another final product`);
      return;
    }
    if (linkAddedSerials.includes(trimmed)) return;
    setLinkAddedSerials(s => [...s, trimmed]);
    setLinkSerialInput('');
  };

  const submitLinkChildren = async () => {
    if (!selectedUnit) {
      toast.error('Complete the final product order first to mint its serial number, then link assemblies.');
      return;
    }
    if (linkAddedSerials.length === 0) { toast.error('Add at least one serial'); return; }
    try {
      const res = await api().post(`/finished-units/${selectedUnit.id}/link-children`, { child_serials: linkAddedSerials });
      const linkedCount = (res.data.linked || []).length;
      toast.success(`Linked ${linkedCount} assemblies`);
      if ((res.data.missing || []).length) toast.warning(`Missing: ${res.data.missing.join(', ')}`);
      if ((res.data.conflicts || []).length) toast.warning(`Already linked: ${res.data.conflicts.map(c => c.serial).join(', ')}`);
      setLinkChildrenOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const openBuildSheet = async (unit) => {
    setSelectedUnit(unit);
    try {
      const res = await api().get(`/finished-units/${unit.id}/build-sheet`);
      setBuildSheet(res.data);
      setBuildSheetOpen(true);
    } catch (error) { toast.error('Failed to load build sheet'); }
  };

  const printBuildSheet = async () => {
    if (!buildSheet) return;
    const html = renderBuildSheetHtml(buildSheet);
    const w = await openLetterheadWindow(html, { title: 'Final Product Build Sheet', subtitle: buildSheet.unit?.serial_number || '' });
    if (w) setTimeout(() => w.print(), 400);
  };

  const printAssemblyTicket = (wo, unit) => {
    // iter78q — open the label dialog (size picker, copies, remember-per-device)
    // instead of immediately spawning a fixed 4x4 print window.
    setLabelTarget({ wo, unit });
    setLabelDialogOpen(true);
  };

  // ===== QC =====
  const openQcRun = (unit) => {
    setSelectedUnit(unit);
    setQcUnitTarget(unit?.id || '');
    setQcTplId('');
    setQcAnswers([]);
    setQcInspectorNotes('');
    setQcRunOpen(true);
  };

  const onPickQcTemplate = (tplId) => {
    setQcTplId(tplId);
    const tpl = qcTemplates.find(t => t.id === tplId);
    setQcAnswers((tpl?.items || []).map(it => ({
      label: it.label,
      item_type: it.item_type,
      pass_fail: it.item_type === 'checkbox' ? null : null,
      measurement: null,
      text_value: '',
      signature: '',
      notes: '',
      _def: it,
    })));
  };

  const submitQc = async (status) => {
    if (!qcTplId) { toast.error('Pick a QC template'); return; }
    const required = qcAnswers.filter(a => a._def?.required);
    for (const a of required) {
      if (a.item_type === 'checkbox' && a.pass_fail === null) { toast.error(`Required: ${a.label}`); return; }
      if (a.item_type === 'measurement' && (a.measurement === null || a.measurement === '')) { toast.error(`Required measurement: ${a.label}`); return; }
      if (a.item_type === 'text' && !a.text_value) { toast.error(`Required note: ${a.label}`); return; }
      if (a.item_type === 'signature' && !a.signature) { toast.error(`Required signature: ${a.label}`); return; }
    }
    try {
      const payload = {
        template_id: qcTplId,
        finished_unit_id: qcUnitTarget || null,
        answers: qcAnswers.map(a => ({
          label: a.label,
          item_type: a.item_type,
          pass_fail: a.pass_fail,
          measurement: a.measurement === '' || a.measurement === null ? null : Number(a.measurement),
          text_value: a.text_value || null,
          signature: a.signature || null,
          notes: a.notes || null,
        })),
        overall_status: status,
        inspector_notes: qcInspectorNotes || null,
      };
      await api().post('/qc-submissions', payload);
      toast.success('QC sheet submitted');
      setQcRunOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const printQcSubmission = async (sub) => {
    const tpl = qcTemplates.find(t => t.id === sub.template_id) || sub.template || {};
    const html = renderQcSheetHtml({ sub, tpl });
    const w = await openLetterheadWindow(html, { title: 'QC Inspection Sheet', subtitle: sub.serial_number || '' });
    if (w) setTimeout(() => w.print(), 400);
  };

  const openQcView = (sub) => { setQcSubView(sub); setQcViewOpen(true); };

  // ===== Render helpers =====
  const formatTime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const getStatusColor = (status) => ({
    'Draft': 'bg-slate-100 text-slate-700',
    'Released': 'bg-blue-100 text-blue-700',
    'InProgress': 'bg-yellow-100 text-yellow-700',
    'Paused': 'bg-orange-100 text-orange-700',
    'Completed': 'bg-green-100 text-green-700',
    'Closed': 'bg-slate-200 text-slate-600',
  }[status] || 'bg-slate-100 text-slate-700');

  const filteredWOs = workOrders.filter(wo => activeCategory === 'all' || wo.queue_category === activeCategory);
  const categoriesForTab = queueCategories.filter(c => c.queue_type === activeTab);
  const partAssemblyUnits = finishedUnits.filter(u => u.unit_kind !== 'final_product');
  const finalUnits = finishedUnits.filter(u => u.unit_kind === 'final_product').sort((a, b) => new Date(b.build_date) - new Date(a.build_date));

  return (
    <div className="space-y-4" data-testid="production-queue-page" data-testid-alt="work-orders-page">
      {/* Active Timer Banner */}
      {activeTimer && (
        <Card className="bg-yellow-50 border-yellow-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Timer className="w-6 h-6 text-yellow-600 animate-pulse" />
                <div>
                  <p className="font-medium">Timer Running</p>
                  <p className="text-sm text-slate-500">WO-{activeTimer.work_order_id?.slice(0, 8).toUpperCase()}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-3xl font-mono font-bold text-yellow-700">{formatTime(elapsedTime)}</span>
                <Button onClick={handlePauseTimer} variant="outline" className="bg-white">
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      {level && (
        <PageHeader
          crumbs={[{ label: 'Build' }, { label: STREAMS[level].label, to: STREAMS[level].base }, { label: 'Build queue' }]}
          title={`${STREAMS[level].singular} Build Queue`} icon={Factory} stream={level} badge={`${STREAMS[level].singular} stream`}
          description={level === 'assembly' ? 'Work orders that build stocked assemblies, including ones auto-created by product builds.' : 'Work orders that build finished products from assemblies and loose parts.'}
          kpis={<>
            <KpiCard stream={level} label="In this tab" value={workOrders.length} testId="queue-kpi-total" />
            <KpiCard stream={level} label="In progress" value={workOrders.filter(w => w.status === 'InProgress').length} testId="queue-kpi-inprogress" />
            <KpiCard stream={level} label="Released" value={workOrders.filter(w => w.status === 'Released').length} testId="queue-kpi-released" />
            <KpiCard stream={level} label="Completed" value={workOrders.filter(w => w.status === 'Completed').length} testId="queue-kpi-completed" />
          </>}
        />
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {!level && <h1 className="text-2xl font-bold">Production Queue</h1>}
          <p className="text-slate-500">{level ? 'Queues' : 'Final products, part-assembly orders and QC inspections'}</p>
        </div>
        <div className="flex items-center gap-3">
          {canApprove && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none border border-slate-200 bg-white rounded-sm px-2.5 py-1.5" title="When ON, approving a work order will notify the assigned operator (in-app + email if available).">
              <input
                type="checkbox"
                checked={notifyOnApprove}
                onChange={(e) => toggleNotifyOnApprove(e.target.checked)}
                className="accent-orange-500"
                data-testid="wo-notify-on-approve-toggle"
              />
              <span>Notify operator on approval</span>
            </label>
          )}
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-orange-500 hover:bg-orange-600" data-testid="create-production-btn">
            <Plus className="w-4 h-4 mr-2" /> New Production
          </Button>
        </div>
      </div>

      {/* iter78r — Production progress widget at top of queue (mirrors dashboard) */}
      <ProductionProgressWidget tvLink={false} />

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setActiveCategory('all'); }}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="forecast" data-testid="tab-forecast" className="flex items-center gap-2">
            <Target className="w-4 h-4" /> Forecast
          </TabsTrigger>
          <TabsTrigger value="finished_production" data-testid="tab-final-products" className="flex items-center gap-2">
            <Package className="w-4 h-4" /> Final Products
          </TabsTrigger>
          <TabsTrigger value="part_assembly" data-testid="tab-part-assembly" className="flex items-center gap-2">
            <Factory className="w-4 h-4" /> Part Assembly
          </TabsTrigger>
          <TabsTrigger value="quality_verification" data-testid="tab-qc-check" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" /> Quality Check
          </TabsTrigger>
        </TabsList>

        {categoriesForTab.length > 0 && (
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button variant={activeCategory === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setActiveCategory('all')}>All</Button>
            {categoriesForTab.map(cat => (
              <Button key={cat.id} variant={activeCategory === cat.category_name ? 'default' : 'outline'} size="sm" onClick={() => setActiveCategory(cat.category_name)}>
                {cat.category_name}
              </Button>
            ))}
          </div>
        )}

        {/* === FORECAST TAB (iter78r) === */}
        <TabsContent value="forecast" className="mt-4 space-y-4">
          <ProductionForecastPanel onCreated={fetchData} />
        </TabsContent>

        {/* === FINAL PRODUCTS TAB === */}
        <TabsContent value="finished_production" className="mt-4 space-y-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Final Product Orders</CardTitle></CardHeader>
            <CardContent className="p-0">
              {filteredWOs.length === 0 ? (
                <EmptyState onCreate={() => { resetForm(); setCreateDialogOpen(true); }} />
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Product / BOM</TableHead>
                      <TableHead>Final Serial</TableHead>
                      <TableHead>Linked Assemblies</TableHead>
                      <TableHead>Approval</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWOs.map(wo => {
                      const pt = ptMap[wo.product_type_id];
                      const bom = bomMap[wo.bom_id];
                      const unit = finalProductUnitFor(wo);
                      const linked = unit?.child_unit_ids?.length || 0;
                      return (
                        <TableRow key={wo.id} data-testid={`final-wo-row-${wo.id}`}>
                          <TableCell className="font-mono font-bold">
                            <button
                              type="button"
                              onClick={() => { setSelectedWO(wo); setDetailDialogOpen(true); }}
                              className="text-orange-600 hover:text-orange-700 hover:underline"
                              title="Open work order detail"
                              data-testid={`final-wo-open-${wo.id}`}
                            >
                              WO-{wo.id.slice(0, 8).toUpperCase()}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{pt?.product_type_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500">{bom?.bom_variant_code} v{bom?.bom_revision}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{unit?.serial_number || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{linked} linked</Badge>
                          </TableCell>
                          <TableCell><ApprovalBadge wo={wo} /></TableCell>
                          <TableCell><span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(wo.status)}`}>{wo.status}</span></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {canApprove && wo.approval_status !== 'approved' && (
                                <Button size="sm" variant="ghost" onClick={() => handleApproveWO(wo)} title="Approve" className="text-emerald-700 hover:bg-emerald-50" data-testid={`wo-approve-${wo.id}`}><ShieldCheck className="w-4 h-4" /></Button>
                              )}
                              {canApprove && wo.approval_status === 'pending' && (
                                <Button size="sm" variant="ghost" onClick={() => handleRejectWO(wo)} title="Reject" className="text-red-600 hover:bg-red-50" data-testid={`wo-reject-${wo.id}`}><ShieldX className="w-4 h-4" /></Button>
                              )}
                              {wo.status === 'Draft' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(wo.id, 'Released')} title="Release"><Play className="w-4 h-4 text-blue-600" /></Button>}
                              {(wo.status === 'Released' || wo.status === 'InProgress') && <Button size="sm" variant="ghost" onClick={() => handleCompleteWO(wo)} title="Complete & mint serial" data-testid={`final-complete-${wo.id}`}><CheckCircle className="w-4 h-4 text-green-600" /></Button>}
                              {(wo.status === 'Completed' || wo.status === 'Closed') && canApprove && (
                                <Button size="sm" variant="ghost" onClick={() => handleFinalizeWO(wo)} title="Finalize: mint serials + QC + sub-BOM cascade" className="text-indigo-700 hover:bg-indigo-50" data-testid={`wo-finalize-${wo.id}`}><Hash className="w-4 h-4" /></Button>
                              )}
                              {unit && <Button size="sm" variant="ghost" onClick={() => openLinkChildren(wo)} title="Link assemblies" data-testid={`final-link-${wo.id}`}><Link2 className="w-4 h-4 text-blue-600" /></Button>}
                              {unit && <Button size="sm" variant="ghost" onClick={() => openBuildSheet(unit)} title="Build sheet" data-testid={`final-buildsheet-${wo.id}`}><Printer className="w-4 h-4" /></Button>}
                              <Button size="sm" variant="ghost" onClick={() => printAssemblyTicket(wo, unit)} title="Print label (pick size)" data-testid={`final-print-label-${wo.id}`}><Tag className="w-4 h-4" /></Button>
                              {(wo.status === 'Completed' || wo.status === 'Closed') && <Button size="sm" variant="ghost" onClick={() => handleReopenWO(wo.id)} title="Reopen"><RotateCcw className="w-4 h-4" /></Button>}
                              <Button size="sm" variant="ghost" onClick={() => { setSelectedWO(wo); setDetailDialogOpen(true); }}><Eye className="w-4 h-4" /></Button>
                              {canApprove && <Button size="sm" variant="ghost" onClick={() => handleDeleteWO(wo)} title="Delete work order" className="text-red-600 hover:bg-red-50" data-testid={`final-delete-${wo.id}`}><Trash2 className="w-4 h-4" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* History */}
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Completed Final Products History</CardTitle></CardHeader>
            <CardContent className="p-0">
              {finalUnits.length === 0 ? (
                <p className="text-sm text-slate-500 p-6 text-center">No completed final products yet.</p>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial #</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>BOM Rev</TableHead>
                      <TableHead>Linked</TableHead>
                      <TableHead>Build Date</TableHead>
                      <TableHead>Built By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {finalUnits.map(u => {
                      const pt = ptMap[u.product_type_id];
                      return (
                        <TableRow key={u.id} data-testid={`final-history-row-${u.id}`}>
                          <TableCell className="font-mono font-bold">{u.serial_number}</TableCell>
                          <TableCell>{pt?.product_type_name || '—'}</TableCell>
                          <TableCell className="text-sm">v{u.bom_revision || '—'}</TableCell>
                          <TableCell><Badge variant="outline">{u.child_unit_ids?.length || 0}</Badge></TableCell>
                          <TableCell className="text-sm">{u.build_date ? new Date(u.build_date).toLocaleString() : '—'}</TableCell>
                          <TableCell className="text-sm">{u.built_by_name || u.built_by_user || '—'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => openBuildSheet(u)} title="Build sheet"><Printer className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === PART ASSEMBLY TAB === */}
        <TabsContent value="part_assembly" className="mt-4 space-y-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Part Assembly Orders</CardTitle></CardHeader>
            <CardContent className="p-0">
              {filteredWOs.length === 0 ? (
                <EmptyState onCreate={() => { resetForm(); setCreateDialogOpen(true); }} />
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Product / BOM</TableHead>
                      <TableHead>BOM Rev</TableHead>
                      <TableHead>Serial #</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Approval</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Built By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWOs.map(wo => {
                      const pt = ptMap[wo.product_type_id];
                      const bom = bomMap[wo.bom_id];
                      const unit = finishedUnits.find(u => u.work_order_id === wo.id);
                      const isTimerActive = activeTimer?.work_order_id === wo.id;
                      return (
                        <TableRow key={wo.id} className={isTimerActive ? 'bg-yellow-50' : ''} data-testid={`part-wo-row-${wo.id}`}>
                          <TableCell className="font-mono font-bold">
                            <button
                              type="button"
                              onClick={() => { setSelectedWO(wo); setDetailDialogOpen(true); }}
                              className="text-orange-600 hover:text-orange-700 hover:underline"
                              title="Open work order detail"
                              data-testid={`part-wo-open-${wo.id}`}
                            >
                              WO-{wo.id.slice(0, 8).toUpperCase()}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{pt?.product_type_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500">{bom?.bom_variant_code}</p>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="font-mono">v{bom?.bom_revision || '—'}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{unit?.serial_number || (wo.status === 'Completed' ? '—' : 'pending')}</TableCell>
                          <TableCell>{wo.completed_quantity}/{wo.target_quantity}</TableCell>
                          <TableCell><ApprovalBadge wo={wo} /></TableCell>
                          <TableCell><span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(wo.status)}`}>{wo.status}</span></TableCell>
                          <TableCell className="text-sm">{unit?.built_by_name || '—'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {canApprove && wo.approval_status !== 'approved' && (
                                <Button size="sm" variant="ghost" onClick={() => handleApproveWO(wo)} title="Approve" className="text-emerald-700 hover:bg-emerald-50" data-testid={`wo-approve-${wo.id}`}><ShieldCheck className="w-4 h-4" /></Button>
                              )}
                              {canApprove && wo.approval_status === 'pending' && (
                                <Button size="sm" variant="ghost" onClick={() => handleRejectWO(wo)} title="Reject" className="text-red-600 hover:bg-red-50" data-testid={`wo-reject-${wo.id}`}><ShieldX className="w-4 h-4" /></Button>
                              )}
                              {wo.status === 'Draft' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(wo.id, 'Released')} title="Release"><Play className="w-4 h-4 text-blue-600" /></Button>}
                              {(wo.status === 'Released' || wo.status === 'Paused') && !isTimerActive && wo.approval_status === 'approved' && <Button size="sm" variant="ghost" onClick={() => handleStartTimer(wo)} title="Start timer" className="text-green-600"><Play className="w-4 h-4" /></Button>}
                              {isTimerActive && <Button size="sm" variant="ghost" onClick={handlePauseTimer} title="Pause" className="text-orange-600"><Pause className="w-4 h-4" /></Button>}
                              {(wo.status === 'InProgress' || wo.status === 'Paused' || wo.status === 'Released') && <Button size="sm" variant="ghost" onClick={() => handleCompleteWO(wo)} title="Complete" className="text-green-600" data-testid={`part-complete-${wo.id}`}><CheckCircle className="w-4 h-4" /></Button>}
                              {(wo.status === 'Completed' || wo.status === 'Closed') && canApprove && (
                                <Button size="sm" variant="ghost" onClick={() => handleFinalizeWO(wo)} title="Finalize: mint serials + QC + sub-BOM cascade" className="text-indigo-700 hover:bg-indigo-50" data-testid={`wo-finalize-${wo.id}`}><Hash className="w-4 h-4" /></Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => printAssemblyTicket(wo, unit)} title="Print label (pick size)" data-testid={`part-print-ticket-${wo.id}`}><Tag className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={async () => {
                                setSelectedWO(wo);
                                setWasteDialogOpen(true);
                                try {
                                  const r = await api().get(`/work-orders/${wo.id}/bom-skus`);
                                  setBomLines(r.data?.lines || []);
                                } catch { setBomLines([]); }
                              }} title="Log waste" className="text-red-600"><AlertTriangle className="w-4 h-4" /></Button>
                              {(wo.status === 'Completed' || wo.status === 'Closed') && <Button size="sm" variant="ghost" onClick={() => handleReopenWO(wo.id)} title="Reopen"><RotateCcw className="w-4 h-4" /></Button>}
                              {canApprove && <Button size="sm" variant="ghost" onClick={() => handleDeleteWO(wo)} title="Delete work order" className="text-red-600 hover:bg-red-50" data-testid={`part-delete-${wo.id}`}><Trash2 className="w-4 h-4" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === QC CHECK TAB === */}
        <TabsContent value="quality_verification" className="mt-4 space-y-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Run a QC Inspection</CardTitle>
              <Button size="sm" onClick={() => openQcRun(null)} className="bg-orange-500 hover:bg-orange-600" data-testid="qc-run-new-btn">
                <Plus className="w-4 h-4 mr-1" /> New QC Sheet
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">
                Pick a QC template (defined in the <a href="/qc-templates" className="text-orange-600 underline">QC Templates</a> page),
                tie the inspection to a Part Assembly or Final Product serial number, fill in the form and submit Pass / Fail.
              </p>
            </CardContent>
          </Card>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">QC History ({qcSubmissions.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {qcSubmissions.length === 0 ? (
                <p className="text-sm text-slate-500 p-6 text-center">No QC submissions yet.</p>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Linked Serial</TableHead>
                      <TableHead>Inspector</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qcSubmissions.map(sub => (
                      <TableRow key={sub.id} data-testid={`qc-sub-row-${sub.id}`}>
                        <TableCell className="text-sm">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '—'}</TableCell>
                        <TableCell className="font-medium">{sub.template_name || '—'}</TableCell>
                        <TableCell className="font-mono text-sm">{sub.serial_number || '—'}</TableCell>
                        <TableCell className="text-sm">{sub.submitted_by_name || '—'}</TableCell>
                        <TableCell>
                          <Badge className={sub.overall_status === 'pass' ? 'bg-emerald-600' : sub.overall_status === 'fail' ? 'bg-red-600' : 'bg-slate-400'}>
                            {sub.overall_status?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openQcView(sub)} data-testid={`qc-sub-view-${sub.id}`}><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => printQcSubmission(sub)} data-testid={`qc-sub-print-${sub.id}`}><Printer className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Create Production Order Dialog ===== */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Production Order</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Queue Type</Label>
              <Select value={formData.queue_type} onValueChange={(v) => setFormData({ ...formData, queue_type: v, is_final_product: v === 'finished_production' })}>
                <SelectTrigger data-testid="create-queue-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="finished_production">Final Product Assembly</SelectItem>
                  <SelectItem value="part_assembly">Part Assembly</SelectItem>
                  <SelectItem value="quality_verification">Quality Verification</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>BOM * <span className="text-xs text-slate-500">— pick a Released BOM, product type auto-fills</span></Label>
              <Select value={formData.bom_id || 'none'} onValueChange={(v) => v !== 'none' && onSelectBom(v)}>
                <SelectTrigger data-testid="create-bom-select">
                  <SelectValue placeholder="Select BOM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>— Select BOM —</SelectItem>
                  {boms.length === 0 && <SelectItem value="empty" disabled>No BOMs in system</SelectItem>}
                  {boms.map(bom => {
                    const pt = ptMap[bom.product_type_id];
                    const isReleased = bom.status === 'Released';
                    return (
                      <SelectItem key={bom.id} value={bom.id} disabled={!isReleased}>
                        <span className={!isReleased ? 'text-slate-400' : ''}>
                          {bom.bom_variant_code} v{bom.bom_revision} · {pt?.product_type_name || '—'}
                          {!isReleased && ` (${bom.status})`}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {formData.bom_id && bomMap[formData.bom_id] && (
                <p className="text-xs text-slate-500" data-testid="create-bom-pt-hint">
                  Product Type: <span className="font-medium text-slate-700">{ptMap[bomMap[formData.bom_id].product_type_id]?.product_type_name || '—'}</span>
                </p>
              )}
            </div>

            {formData.queue_type === 'finished_production' && families.length > 0 && (
              <div className="space-y-2">
                <Label>Product Family (optional checklist)</Label>
                <Select value={formData.family_id || 'none'} onValueChange={(v) => setFormData({ ...formData, family_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="create-family-select"><SelectValue placeholder="No family" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No family</SelectItem>
                    {families.filter(f => f.active !== false).map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.family_name} ({(f.member_product_type_ids || []).length} members)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.queue_category || 'none'} onValueChange={(v) => setFormData({ ...formData, queue_category: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {queueCategories.filter(c => c.queue_type === formData.queue_type).map(cat => (
                    <SelectItem key={cat.id} value={cat.category_name}>{cat.category_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={1} readOnly disabled className="bg-slate-100 mono" data-testid="create-qty-input" />
                  <span className="text-xs text-slate-500">locked to 1 / WO</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Priority (1-10)</Label>
                <Input type="number" min="1" max="10" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 5 })} />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm select-none rounded-sm border border-slate-200 p-2 bg-emerald-50/50">
              <input
                type="checkbox"
                checked={!!formData.auto_release}
                onChange={(e) => setFormData({ ...formData, auto_release: e.target.checked })}
                data-testid="create-auto-release"
                className="h-4 w-4"
              />
              <span><strong>Release immediately</strong> — operators can clock in right away (recommended)</span>
            </label>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date || ''}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                data-testid="create-due-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateWO} disabled={loading} className="bg-orange-500 hover:bg-orange-600" data-testid="create-submit-btn">
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waste */}
      <Dialog open={wasteDialogOpen} onOpenChange={setWasteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Waste / Failure</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={wasteData.waste_type} onValueChange={(v) => setWasteData({ ...wasteData, waste_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Damaged', 'Rejected', 'Replaced', 'Scrap', 'Rework'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" value={wasteData.quantity} onChange={(e) => setWasteData({ ...wasteData, quantity: parseFloat(e.target.value) || 1 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Item SKU {bomLines.length > 0 && <span className="text-xs text-orange-600">— pick from BOM or type a custom SKU</span>}</Label>
              {bomLines.length > 0 && (
                <Select value={wasteData.item_sku} onValueChange={(v) => {
                  const ln = bomLines.find(l => l.component_sku === v);
                  setWasteData({ ...wasteData, item_sku: v, item_name: ln?.component_name || '' });
                }}>
                  <SelectTrigger data-testid="waste-bom-picker"><SelectValue placeholder="Pick from BOM…" /></SelectTrigger>
                  <SelectContent>
                    {bomLines.map(l => (
                      <SelectItem key={l.component_sku} value={l.component_sku}>
                        {l.component_sku} — {l.component_name} {l.is_critical && '⚠'} (on hand: {l.current_quantity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Input value={wasteData.item_sku} onChange={(e) => setWasteData({ ...wasteData, item_sku: e.target.value })} placeholder="e.g., COMP-001" data-testid="waste-sku-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={wasteData.reason} onChange={(e) => setWasteData({ ...wasteData, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWasteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLogWaste} className="bg-red-500 hover:bg-red-600">Log Waste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter78q — WO label print dialog (label-size picker, copies, preview) */}
      <WorkOrderLabelDialog
        open={labelDialogOpen}
        onOpenChange={setLabelDialogOpen}
        wo={labelTarget.wo}
        unit={labelTarget.unit}
        bom={labelTarget.wo ? bomMap[labelTarget.wo.bom_id] : null}
        pt={labelTarget.wo ? ptMap[labelTarget.wo.product_type_id] : null}
      />

      {/* Detail */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Production Order Details</span>
              {selectedWO && (
                <a
                  href={`/work-orders/${selectedWO.id}`}
                  className="text-xs text-orange-600 hover:underline font-normal flex items-center gap-1 mr-6"
                  data-testid="wo-detail-open-full-page"
                >
                  Open full page ↗
                </a>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedWO && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-slate-500">Order #</p><p className="font-mono font-bold">WO-{selectedWO.id.slice(0, 8).toUpperCase()}</p></div>
                <div><p className="text-slate-500">Status</p><span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(selectedWO.status)}`}>{selectedWO.status}</span></div>
                <div><p className="text-slate-500">Queue</p><p>{selectedWO.queue_type?.replace('_', ' ')}</p></div>
                <div><p className="text-slate-500">Target Qty</p><p>{selectedWO.completed_quantity} / {selectedWO.target_quantity}</p></div>
                <div><p className="text-slate-500">Created</p><p>{new Date(selectedWO.created_date).toLocaleString()}</p></div>
                <div><p className="text-slate-500">Total Time</p><p>{(selectedWO.total_time_minutes || 0).toFixed(1)} min</p></div>
                {selectedWO.due_date && <div><p className="text-slate-500">Due Date</p><p>{selectedWO.due_date}</p></div>}
                {selectedWO.notes && <div className="col-span-2"><p className="text-slate-500">Notes</p><p className="bg-slate-50 p-2 rounded text-sm">{selectedWO.notes}</p></div>}
              </div>

              {/* iter78 — per-operator elapsed time breakdown */}
              <div className="border-t border-slate-200 pt-3" data-testid="wo-time-breakdown">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-slate-700">Time spent by operator</p>
                  {woTimeData?.summary && (
                    <p className="text-xs text-slate-500">
                      {woTimeData.summary.total_entries} entr{woTimeData.summary.total_entries === 1 ? 'y' : 'ies'} •{' '}
                      <span className="font-mono">{(woTimeData.summary.total_minutes || 0).toFixed(1)} min</span>
                      {' '}({(woTimeData.summary.total_hours || 0).toFixed(2)} h)
                    </p>
                  )}
                </div>
                {woTimeLoading ? (
                  <p className="text-xs text-slate-500">Loading time entries…</p>
                ) : !woTimeData || (woTimeData.entries || []).length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No time entries logged for this work order yet.</p>
                ) : (() => {
                  const byUser = {};
                  (woTimeData.entries || []).forEach((e) => {
                    const k = e.user_id || 'unknown';
                    if (!byUser[k]) byUser[k] = { user_id: k, minutes: 0, units: 0, sessions: 0, active: false };
                    byUser[k].minutes += e.duration_minutes || 0;
                    byUser[k].units += e.units_completed || 0;
                    byUser[k].sessions += 1;
                    if (e.is_active) byUser[k].active = true;
                  });
                  const rows = Object.values(byUser).sort((a, b) => b.minutes - a.minutes);
                  return (
                    <div className="rounded-md border border-slate-200 divide-y divide-slate-200">
                      {rows.map((r) => (
                        <div key={r.user_id} className="flex items-center justify-between px-3 py-2" data-testid={`wo-time-row-${r.user_id}`}>
                          <div>
                            <p className="font-medium">{userNameMap[r.user_id] || r.user_id?.slice(0, 8) || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">
                              {r.sessions} session{r.sessions === 1 ? '' : 's'}
                              {r.units > 0 ? ` • ${r.units} units` : ''}
                              {r.active ? ' • clocked in now' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold">{r.minutes.toFixed(1)} min</p>
                            <p className="text-xs text-slate-500">{(r.minutes / 60).toFixed(2)} h</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Children Dialog */}
      <Dialog open={linkChildrenOpen} onOpenChange={setLinkChildrenOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Link Part-Assembly Serials to Final Product</DialogTitle></DialogHeader>
          {!selectedUnit ? (
            <p className="text-sm text-slate-500 py-4">Complete the final product order first to mint its serial. Then come back here to link.</p>
          ) : (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-sm p-3 text-sm">
                <p>Final Product Serial: <span className="font-mono font-bold">{selectedUnit.serial_number}</span></p>
                <p className="text-xs text-slate-500">Existing children: {selectedUnit.child_unit_ids?.length || 0}</p>
              </div>
              <FamilyChecklist unit={selectedUnit} families={families} ptMap={ptMap} fuById={fuById} />
              <div>
                <Label>Add Part-Assembly Serial</Label>
                <div className="flex gap-2">
                  <Input value={linkSerialInput} onChange={e => setLinkSerialInput(e.target.value)} placeholder="Type or scan serial #" data-testid="link-serial-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSerialToLink(); } }} />
                  <Button onClick={() => addSerialToLink()} data-testid="link-serial-add-btn">Add</Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Press Enter to add. Browse below for completed assemblies not yet linked.</p>
              </div>
              <div className="border border-slate-200 rounded-sm p-2 max-h-48 overflow-y-auto">
                <p className="text-xs text-slate-500 mb-1">Available part-assembly units:</p>
                {partAssemblyUnits.filter(u => !u.parent_finished_unit_id || u.parent_finished_unit_id === selectedUnit.id).slice(0, 30).map(u => (
                  <button type="button" key={u.id}
                    onClick={() => addSerialToLink(u.serial_number)}
                    className="w-full text-left text-sm py-1 px-2 rounded-sm hover:bg-slate-100 flex items-center justify-between"
                    data-testid={`link-pick-${u.id}`}>
                    <span className="font-mono">{u.serial_number}</span>
                    <span className="text-xs text-slate-500">{ptMap[u.product_type_id]?.product_type_name || ''}</span>
                  </button>
                ))}
                {partAssemblyUnits.length === 0 && <p className="text-xs text-slate-400 p-2">No part-assembly units available.</p>}
              </div>
              {linkAddedSerials.length > 0 && (
                <div>
                  <Label>To be linked ({linkAddedSerials.length})</Label>
                  <div className="flex flex-wrap gap-1">
                    {linkAddedSerials.map(s => (
                      <Badge key={s} variant="outline" className="font-mono">
                        {s}
                        <Unlink className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setLinkAddedSerials(arr => arr.filter(x => x !== s))} />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkChildrenOpen(false)}>Close</Button>
            <Button onClick={submitLinkChildren} disabled={!selectedUnit || linkAddedSerials.length === 0} className="bg-orange-500 hover:bg-orange-600" data-testid="link-submit-btn">
              Link {linkAddedSerials.length} Assemblies
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Build Sheet */}
      <Dialog open={buildSheetOpen} onOpenChange={setBuildSheetOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Final Product Build Sheet</DialogTitle></DialogHeader>
          {buildSheet && (
            <div className="space-y-4 text-sm" data-testid="build-sheet-content">
              <div className="grid grid-cols-3 gap-3 bg-slate-50 rounded-sm p-3">
                <div><p className="text-xs text-slate-500">Final Serial</p><p className="font-mono font-bold">{buildSheet.unit?.serial_number}</p></div>
                <div><p className="text-xs text-slate-500">Product</p><p>{buildSheet.product_type || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Build Date</p><p>{buildSheet.unit?.build_date ? new Date(buildSheet.unit.build_date).toLocaleString() : '—'}</p></div>
                <div><p className="text-xs text-slate-500">Built By</p><p>{buildSheet.unit?.built_by_name || '—'}</p></div>
                <div><p className="text-xs text-slate-500">BOM Rev</p><p>v{buildSheet.unit?.bom_revision || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Family</p><p>{buildSheet.family?.name || '—'}</p></div>
              </div>
              {buildSheet.checklist?.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Family Checklist</p>
                  <ul className="space-y-1">
                    {buildSheet.checklist.map((c, i) => (
                      <li key={i} className={`flex items-center justify-between rounded-sm px-3 py-1.5 text-sm ${c.satisfied ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        <span>{c.product_type_name}</span>
                        <span className="font-mono text-xs">{c.linked_serials.length ? c.linked_serials.join(', ') : 'MISSING'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <p className="font-semibold mb-1">Linked Part Assemblies ({buildSheet.children?.length || 0})</p>
                {buildSheet.children?.length === 0 ? <p className="text-xs text-slate-500">None linked yet.</p> : (
                  <Table className="data-table">
                    <TableHeader><TableRow>
                      <TableHead>Serial</TableHead><TableHead>Product</TableHead><TableHead>BOM</TableHead><TableHead>Rev</TableHead><TableHead>Built</TableHead><TableHead>By</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {buildSheet.children.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono">{c.serial_number}</TableCell>
                          <TableCell>{c.product_type || '—'}</TableCell>
                          <TableCell>{c.bom_variant_code || '—'}</TableCell>
                          <TableCell>v{c.bom_revision || '—'}</TableCell>
                          <TableCell className="text-xs">{c.build_date ? new Date(c.build_date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell className="text-xs">{c.built_by_name || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              {buildSheet.qc_submissions?.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Linked QC Submissions ({buildSheet.qc_submissions.length})</p>
                  <ul className="space-y-1 text-sm">
                    {buildSheet.qc_submissions.map(q => (
                      <li key={q.id} className="flex items-center justify-between bg-slate-50 rounded-sm px-2 py-1">
                        <span>{q.template_name}</span>
                        <Badge className={q.overall_status === 'pass' ? 'bg-emerald-600' : q.overall_status === 'fail' ? 'bg-red-600' : 'bg-slate-400'}>{q.overall_status?.toUpperCase()}</Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuildSheetOpen(false)}>Close</Button>
            <Button onClick={printBuildSheet} className="bg-orange-500 hover:bg-orange-600" data-testid="build-sheet-print-btn">
              <Printer className="w-4 h-4 mr-1" /> Print Build Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC Run Dialog */}
      <Dialog open={qcRunOpen} onOpenChange={setQcRunOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New QC Sheet</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>QC Template *</Label>
                <Select value={qcTplId || 'none'} onValueChange={(v) => v !== 'none' && onPickQcTemplate(v)}>
                  <SelectTrigger data-testid="qc-tpl-select"><SelectValue placeholder="Select template…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>— Select —</SelectItem>
                    {qcTemplates.length === 0 && <SelectItem value="empty" disabled>No active templates — define them in QC Templates</SelectItem>}
                    {qcTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.template_name} <span className="text-xs text-slate-500">({(t.applies_to || 'any').replace('_', ' ')})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Linked Finished Unit (optional)</Label>
                <Select value={qcUnitTarget || 'none'} onValueChange={(v) => setQcUnitTarget(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="qc-target-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No target</SelectItem>
                    {finishedUnits.slice(0, 50).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.serial_number} · {u.unit_kind === 'final_product' ? 'Final' : 'Part'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {qcAnswers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base">Inspection Items</Label>
                {qcAnswers.map((a, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-sm p-3" data-testid={`qc-answer-${idx}`}>
                    <p className="font-medium text-sm">{a.label} {a._def?.required && <span className="text-red-500">*</span>}</p>
                    {a._def?.description && <p className="text-xs text-slate-500 mb-2">{a._def.description}</p>}
                    {a.item_type === 'checkbox' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant={a.pass_fail === true ? 'default' : 'outline'} className={a.pass_fail === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          onClick={() => setQcAnswers(arr => arr.map((x, i) => i === idx ? { ...x, pass_fail: true } : x))} data-testid={`qc-pass-${idx}`}>Pass</Button>
                        <Button size="sm" variant={a.pass_fail === false ? 'default' : 'outline'} className={a.pass_fail === false ? 'bg-red-600 hover:bg-red-700' : ''}
                          onClick={() => setQcAnswers(arr => arr.map((x, i) => i === idx ? { ...x, pass_fail: false } : x))} data-testid={`qc-fail-${idx}`}>Fail</Button>
                      </div>
                    )}
                    {a.item_type === 'measurement' && (
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="col-span-2">
                          <Label className="text-xs">Reading {a._def?.unit ? `(${a._def.unit})` : ''}</Label>
                          <Input type="number" value={a.measurement ?? ''} onChange={(e) => setQcAnswers(arr => arr.map((x, i) => i === idx ? { ...x, measurement: e.target.value } : x))} data-testid={`qc-meas-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-xs">Target / Range</Label>
                          <p className="text-xs py-2">{a._def?.target || ''} {a._def?.min_value !== null && a._def?.max_value !== null ? `[${a._def.min_value} – ${a._def.max_value}]` : ''}</p>
                        </div>
                      </div>
                    )}
                    {a.item_type === 'text' && (
                      <Textarea value={a.text_value} onChange={(e) => setQcAnswers(arr => arr.map((x, i) => i === idx ? { ...x, text_value: e.target.value } : x))} placeholder="Note…" rows={2} data-testid={`qc-text-${idx}`} />
                    )}
                    {a.item_type === 'signature' && (
                      <Input value={a.signature} onChange={(e) => setQcAnswers(arr => arr.map((x, i) => i === idx ? { ...x, signature: e.target.value } : x))} placeholder="Type your name to sign" data-testid={`qc-sig-${idx}`} />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label>Inspector Notes</Label>
              <Textarea value={qcInspectorNotes} onChange={e => setQcInspectorNotes(e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcRunOpen(false)}>Cancel</Button>
            <Button onClick={() => submitQc('fail')} className="bg-red-600 hover:bg-red-700" data-testid="qc-submit-fail-btn">Submit FAIL</Button>
            <Button onClick={() => submitQc('pass')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="qc-submit-pass-btn">Submit PASS</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QC View */}
      <Dialog open={qcViewOpen} onOpenChange={setQcViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>QC Submission</DialogTitle></DialogHeader>
          {qcSubView && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-xs text-slate-500">Template</p><p className="font-medium">{qcSubView.template_name}</p></div>
                <div><p className="text-xs text-slate-500">Result</p><Badge className={qcSubView.overall_status === 'pass' ? 'bg-emerald-600' : 'bg-red-600'}>{qcSubView.overall_status?.toUpperCase()}</Badge></div>
                <div><p className="text-xs text-slate-500">Linked Serial</p><p className="font-mono">{qcSubView.serial_number || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Inspector</p><p>{qcSubView.submitted_by_name || '—'}</p></div>
                <div><p className="text-xs text-slate-500">Submitted</p><p>{qcSubView.submitted_at ? new Date(qcSubView.submitted_at).toLocaleString() : '—'}</p></div>
              </div>
              <div className="border-t border-slate-200 pt-2">
                <p className="font-semibold mb-2">Answers</p>
                <ul className="space-y-1">
                  {(qcSubView.answers || []).map((a, i) => (
                    <li key={i} className="flex items-center justify-between bg-slate-50 rounded-sm px-2 py-1">
                      <span>{a.label}</span>
                      <span className="text-xs">
                        {a.item_type === 'checkbox' && (a.pass_fail === true ? <Badge className="bg-emerald-600">PASS</Badge> : a.pass_fail === false ? <Badge className="bg-red-600">FAIL</Badge> : '—')}
                        {a.item_type === 'measurement' && <span className="font-mono">{a.measurement ?? '—'}</span>}
                        {a.item_type === 'text' && <span>{a.text_value || '—'}</span>}
                        {a.item_type === 'signature' && <span>✍ {a.signature || '—'}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {qcSubView.inspector_notes && (
                <div className="bg-slate-50 rounded-sm p-2"><p className="text-xs text-slate-500">Inspector Notes</p><p>{qcSubView.inspector_notes}</p></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcViewOpen(false)}>Close</Button>
            <Button onClick={() => qcSubView && printQcSubmission(qcSubView)} className="bg-orange-500 hover:bg-orange-600">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmptyState = ({ onCreate }) => (
  <div className="flex flex-col items-center justify-center h-48 text-slate-500">
    <Factory className="w-12 h-12 mb-3 text-slate-300" />
    <p>No production orders in this queue</p>
    <Button variant="link" onClick={onCreate} className="text-orange-600">Create your first order</Button>
  </div>
);

const FamilyChecklist = ({ unit, families, ptMap, fuById }) => {
  const family = families.find(f => f.id === unit?.family_id);
  if (!family) return null;
  const childUnits = (unit.child_unit_ids || []).map(id => fuById[id]).filter(Boolean);
  return (
    <div className="border border-slate-200 rounded-sm p-3">
      <p className="text-sm font-semibold mb-1">Family Checklist · {family.family_name}</p>
      <ul className="space-y-1">
        {(family.member_product_type_ids || []).map(mid => {
          const present = childUnits.filter(c => c.product_type_id === mid);
          const ok = present.length > 0;
          return (
            <li key={mid} className={`flex items-center justify-between rounded-sm px-2 py-1 text-sm ${ok ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <span>{ptMap[mid]?.product_type_name || '—'}</span>
              <span className="text-xs font-mono">{ok ? present.map(p => p.serial_number).join(', ') : 'MISSING'}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ====== Print HTML helpers ======
const htmlEscape = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// =================================================================
// iter78r — Production Forecast Panel
// Shows the active weekly target broken down by product type, what's been
// built so far, how many WOs are already in-flight, and the remaining gap.
// One-click "Generate" button spawns the missing WOs so the floor can start.
// =================================================================
const ProductionForecastPanel = ({ onCreated }) => {
  const { api, user } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [offset, setOffset] = useState(0); // iter78s — 0 = current window, +1 = next
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyPt, setBusyPt] = useState(null);
  const canCreate = user?.role === 'Admin' || user?.role === 'ProductionSupervisor';

  // Compute ref_date for the forecast window based on offset
  const refDate = useMemo(() => {
    if (offset === 0) return null;
    const today = new Date();
    const stride = period === 'daily' ? 1 : period === 'monthly' ? 30 : 7;
    today.setDate(today.getDate() + offset * stride);
    return today.toISOString().slice(0, 10);
  }, [period, offset]);

  const fetchForecast = async () => {
    setLoading(true);
    try {
      const q = refDate ? `?period_type=${period}&ref_date=${refDate}` : `?period_type=${period}`;
      const r = await api().get(`/dashboard/production-forecast${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchForecast(); /* eslint-disable-next-line */ }, [period, offset]);

  const generate = async (row, count) => {
    if (!row.default_bom_id) {
      toast.error(`No released BOM for ${row.product_type_name}. Release one in BOMs first.`);
      return;
    }
    const qty = parseInt(count, 10);
    if (!qty || qty <= 0) return;
    if (!window.confirm(`Generate ${qty} auto-released work order${qty === 1 ? '' : 's'} for ${row.product_type_name}?\nBOM: ${row.default_bom_label || row.default_bom_id}`)) return;
    setBusyPt(row.product_type_id);
    try {
      const r = await api().post('/dashboard/production-forecast/generate-wos', {
        product_type_id: row.product_type_id,
        count: qty,
        bom_id: row.default_bom_id,
        queue_type: 'finished_production',
        auto_release: true,
      });
      toast.success(`${r.data.created} WO${r.data.created === 1 ? '' : 's'} created and released`);
      await fetchForecast();
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate WOs');
    } finally {
      setBusyPt(null);
    }
  };

  const win = data?.window;
  const rows = data?.rows || [];
  const totals = data?.totals || { target: 0, built: 0, in_flight: 0, gap: 0 };
  const hasTarget = (data?.target?.target_count_total || 0) > 0 || rows.some(r => r.target > 0);
  const periodLabel = period === 'daily' ? 'day' : period === 'monthly' ? 'month' : 'week';
  const offsetLabel = offset === 0 ? `This ${periodLabel}` : offset === 1 ? `Next ${periodLabel}` : offset === -1 ? `Last ${periodLabel}` : `${offset > 0 ? '+' : ''}${offset} ${periodLabel}s`;
  const isHeadStart = offset > 0;

  return (
    <div className="space-y-4">
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="w-4 h-4 text-orange-600" /> Production Forecast
            {win && <span className="text-xs font-normal text-slate-500">· {win.start} → {win.end}</span>}
            {offset !== 0 && (
              <Badge variant="outline" className={`text-[10px] ${isHeadStart ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-slate-300 text-slate-600'}`}>
                {offsetLabel}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-sm border border-slate-200 bg-white overflow-hidden" data-testid="forecast-offset-group">
              <button type="button" onClick={() => setOffset(o => o - 1)} className="px-2 py-1 text-xs hover:bg-slate-100" title={`Previous ${periodLabel}`} data-testid="forecast-prev-btn">←</button>
              <button type="button" onClick={() => setOffset(0)} className={`px-3 py-1 text-xs ${offset === 0 ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`} data-testid="forecast-current-btn">Now</button>
              <button type="button" onClick={() => setOffset(o => o + 1)} className="px-2 py-1 text-xs hover:bg-slate-100" title={`Next ${periodLabel}`} data-testid="forecast-next-btn">→</button>
            </div>
            <Select value={period} onValueChange={(v) => { setPeriod(v); setOffset(0); }}>
              <SelectTrigger className="w-32 rounded-sm" data-testid="forecast-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={fetchForecast} disabled={loading} data-testid="forecast-refresh-btn">
              <RotateCcw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-4 gap-3 text-center">
            <ForecastStat label="Target" value={totals.target} tone="slate" testId="forecast-stat-target" />
            <ForecastStat label="Built" value={totals.built} tone="emerald" testId="forecast-stat-built" />
            <ForecastStat label="In Flight" value={totals.in_flight} tone="amber" testId="forecast-stat-inflight" />
            <ForecastStat label="Gap" value={totals.gap} tone={totals.gap > 0 ? 'red' : 'emerald'} testId="forecast-stat-gap" />
          </div>

          {!hasTarget ? (
            <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="forecast-empty">
              <Target className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-medium text-slate-700">No target set for this {period} window.</p>
              <p className="text-xs text-slate-500 mt-1">Scroll up to the Production Progress widget and click "Set Target" to add this week's forecast by product type.</p>
            </div>
          ) : (
            <Table className="data-table" data-testid="forecast-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Product Type</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Built</TableHead>
                  <TableHead className="text-right">In Flight</TableHead>
                  <TableHead className="text-right">Gap</TableHead>
                  <TableHead>BOM</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => {
                  const pct = r.target > 0 ? Math.min(100, Math.round(((r.built + r.in_flight) / r.target) * 100)) : 0;
                  return (
                    <TableRow key={r.product_type_id} data-testid={`forecast-row-${r.product_type_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{r.product_type_name}</p>
                          <div className="mt-1 bg-slate-100 rounded-sm h-1.5 w-32 overflow-hidden">
                            <div className={`h-full ${r.gap === 0 ? 'bg-emerald-500' : 'bg-orange-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">{r.target}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{r.built}</TableCell>
                      <TableCell className="text-right font-mono text-amber-700">{r.in_flight}</TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {r.gap > 0 ? <span className="text-red-600">{r.gap}</span> : <span className="text-emerald-600">0</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {r.default_bom_label || <span className="text-red-500 italic">no released BOM</span>}
                      </TableCell>
                      <TableCell>
                        {r.gap > 0 && canCreate && r.default_bom_id ? (
                          <Button
                            size="sm"
                            onClick={() => generate(r, r.gap)}
                            disabled={busyPt === r.product_type_id}
                            className={isHeadStart ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-500 hover:bg-orange-600'}
                            data-testid={`forecast-generate-${r.product_type_id}`}
                            title={isHeadStart ? `Pre-create ${r.gap} WOs for ${offsetLabel.toLowerCase()}` : `Auto-create ${r.gap} WOs to close the gap`}
                          >
                            <Zap className="w-3.5 h-3.5 mr-1" />
                            {busyPt === r.product_type_id ? 'Generating…' : isHeadStart ? `Head-start ${r.gap}` : `Generate ${r.gap}`}
                          </Button>
                        ) : r.gap === 0 ? (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700">On target</Badge>
                        ) : !r.default_bom_id ? (
                          <span className="text-xs text-slate-400">Release a BOM first</span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <p className="text-[11px] text-slate-500 leading-snug">
            Built = final-product serials minted in the window. In Flight = open WOs (Draft / Released / In-Progress / Paused) that will mint a final serial.
            Completing a WO auto-ticks Built and reduces the Gap on this view.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const ForecastStat = ({ label, value, tone, testId }) => {
  const toneMap = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-sm p-3 ${toneMap[tone] || toneMap.slate}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest opacity-70">{label}</p>
      <p className="font-mono font-bold text-2xl mt-1">{value}</p>
    </div>
  );
};

const renderAssemblyTicketHtml = ({ wo, unit, bom, pt }) => {
  // iter78l — completion label: drop the per-component list (shortens the label)
  // and add a QR pointing at the finished-unit (or WO if not yet built).
  const qrTarget = unit?.serial_number
    ? `${window.location.origin}/finished-units/${unit.id}`
    : `${window.location.origin}/work-orders/${wo.id}`;
  const qrSvg = renderToStaticMarkup(
    <QRCodeSVG value={qrTarget} size={140} level="M" includeMargin={false} />
  );
  return `<!doctype html><html><head><title>BOM Ticket</title>
  <style>
    @page { size: 4in 4in; margin: 0.2in; }
    body { font-family: -apple-system, system-ui, Arial, sans-serif; color:#1a202c; font-size:11px; margin: 0; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .row { display: flex; gap: 8px; align-items: flex-start; }
    .meta { flex: 1; display:grid; grid-template-columns: 1fr 1fr; gap:4px; }
    .qr { flex: 0 0 auto; padding: 2px; background: #fff; border: 1px solid #cbd5e1; border-radius: 2px; }
    .label { color:#64748b; font-size:10px; }
    .value { font-weight:600; }
    .footer { margin-top:10px; border-top:1px solid #cbd5e1; padding-top:6px; }
  </style></head><body>
  <h1>BOM TICKET — Part Assembly</h1>
  <div class="row">
    <div class="meta">
      <div><div class="label">WO #</div><div class="value">WO-${wo.id.slice(0, 8).toUpperCase()}</div></div>
      <div><div class="label">Build Date</div><div class="value">${unit?.build_date ? new Date(unit.build_date).toLocaleDateString() : '—'}</div></div>
      <div><div class="label">Product Type</div><div class="value">${htmlEscape(pt?.product_type_name)}</div></div>
      <div><div class="label">BOM</div><div class="value">${htmlEscape(bom?.bom_variant_code)} v${htmlEscape(bom?.bom_revision)}</div></div>
      <div><div class="label">Serial #</div><div class="value">${htmlEscape(unit?.serial_number || 'pending')}</div></div>
      <div><div class="label">Built By</div><div class="value">${htmlEscape(unit?.built_by_name || '—')}</div></div>
    </div>
    <div class="qr">${qrSvg}</div>
  </div>
  <div class="footer">
    <p><span class="label">Assembler Signature:</span> _________________________________</p>
    <p><span class="label">QC Signature:</span> _________________________________</p>
  </div>
  </body></html>`;
};

const renderBuildSheetHtml = (data) => {
  const childRows = (data.children || []).map(c => `<tr><td>${htmlEscape(c.serial_number)}</td><td>${htmlEscape(c.product_type)}</td><td>${htmlEscape(c.bom_variant_code || '')} v${htmlEscape(c.bom_revision || '')}</td><td>${c.build_date ? new Date(c.build_date).toLocaleDateString() : '—'}</td><td>${htmlEscape(c.built_by_name)}</td></tr>`).join('');
  const qcRows = (data.qc_submissions || []).map(q => `<tr><td>${htmlEscape(q.template_name)}</td><td>${(q.overall_status || '').toUpperCase()}</td><td>${htmlEscape(q.submitted_by_name || '')}</td><td>${q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : ''}</td></tr>`).join('');
  const checklistRows = (data.checklist || []).map(c => `<tr><td>${htmlEscape(c.product_type_name)}</td><td>${c.satisfied ? '✓ ' + (c.linked_serials || []).join(', ') : '✗ MISSING'}</td></tr>`).join('');
  return `<!doctype html><html><head><title>Build Sheet</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    body { font-family: -apple-system, system-ui, Arial, sans-serif; color:#0f172a; font-size:12px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    h2 { font-size: 14px; margin: 12px 0 4px; border-bottom:1px solid #cbd5e1; padding-bottom:2px; }
    .grid { display:grid; grid-template-columns: 1fr 1fr 1fr; gap:6px; margin: 8px 0; }
    .cell { background:#f1f5f9; padding:6px 8px; border-radius:2px; }
    .label { color:#64748b; font-size:10px; }
    .value { font-weight:600; font-size:13px; }
    table { width:100%; border-collapse:collapse; margin-top:4px; }
    th, td { padding:4px 6px; border-bottom:1px solid #cbd5e1; text-align:left; }
    th { background:#e2e8f0; }
  </style></head><body>
  <!--LETTERHEAD-->
  <div class="grid">
    <div class="cell"><div class="label">Final Serial</div><div class="value">${htmlEscape(data.unit?.serial_number)}</div></div>
    <div class="cell"><div class="label">Product</div><div class="value">${htmlEscape(data.product_type)}</div></div>
    <div class="cell"><div class="label">Family</div><div class="value">${htmlEscape(data.family?.name || '—')}</div></div>
    <div class="cell"><div class="label">Build Date</div><div class="value">${data.unit?.build_date ? new Date(data.unit.build_date).toLocaleString() : '—'}</div></div>
    <div class="cell"><div class="label">Built By</div><div class="value">${htmlEscape(data.unit?.built_by_name || '—')}</div></div>
    <div class="cell"><div class="label">BOM Rev</div><div class="value">v${htmlEscape(data.unit?.bom_revision || '—')}</div></div>
  </div>
  ${data.checklist?.length ? `<h2>Family Checklist</h2><table><tr><th>Product Type</th><th>Status / Linked Serials</th></tr>${checklistRows}</table>` : ''}
  <h2>Linked Part Assemblies (${(data.children || []).length})</h2>
  <table><tr><th>Serial #</th><th>Product</th><th>BOM</th><th>Build Date</th><th>Built By</th></tr>${childRows || '<tr><td colspan="5">None linked.</td></tr>'}</table>
  ${qcRows ? `<h2>QC Submissions</h2><table><tr><th>Template</th><th>Result</th><th>Inspector</th><th>Date</th></tr>${qcRows}</table>` : ''}
  <div style="margin-top:24px"><p>Final Inspector Signature: _____________________________ Date: _____________</p></div>
  </body></html>`;
};

const renderQcSheetHtml = ({ sub, tpl }) => {
  const items = (tpl.items || []).map((it, i) => {
    const ans = (sub.answers || [])[i] || {};
    let answer = '—';
    if (it.item_type === 'checkbox') answer = ans.pass_fail === true ? '☑ PASS' : ans.pass_fail === false ? '☒ FAIL' : '☐';
    if (it.item_type === 'measurement') answer = `${ans.measurement ?? '—'} ${it.unit || ''}`;
    if (it.item_type === 'text') answer = htmlEscape(ans.text_value || '—');
    if (it.item_type === 'signature') answer = `✍ ${htmlEscape(ans.signature || '—')}`;
    return `<tr><td>${htmlEscape(it.label)}</td><td>${it.item_type}</td><td>${answer}</td></tr>`;
  }).join('');
  return `<!doctype html><html><head><title>QC Sheet</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    body { font-family: -apple-system, system-ui, Arial, sans-serif; color:#0f172a; font-size:12px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .meta { display:grid; grid-template-columns: 1fr 1fr; gap:4px; margin: 8px 0; }
    .label { color:#64748b; font-size:10px; }
    .value { font-weight:600; }
    table { width:100%; border-collapse:collapse; margin-top:4px; }
    th, td { padding:4px 6px; border-bottom:1px solid #cbd5e1; text-align:left; }
    th { background:#e2e8f0; }
    .pass { color:#047857; font-weight:bold; }
    .fail { color:#b91c1c; font-weight:bold; }
  </style></head><body>
  <!--LETTERHEAD-->
  <div class="meta">
    <div><div class="label">Template</div><div class="value">${htmlEscape(sub.template_name)}</div></div>
    <div><div class="label">Result</div><div class="value ${sub.overall_status}">${(sub.overall_status || '').toUpperCase()}</div></div>
    <div><div class="label">Linked Serial</div><div class="value">${htmlEscape(sub.serial_number || '—')}</div></div>
    <div><div class="label">Inspector</div><div class="value">${htmlEscape(sub.submitted_by_name || '—')}</div></div>
    <div><div class="label">Submitted</div><div class="value">${sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : '—'}</div></div>
  </div>
  <table><tr><th>Item</th><th>Type</th><th>Result</th></tr>${items || '<tr><td colspan="3">No items</td></tr>'}</table>
  ${sub.inspector_notes ? `<div style="margin-top:8px"><div class="label">Inspector Notes</div><div>${htmlEscape(sub.inspector_notes)}</div></div>` : ''}
  <div style="margin-top:24px"><p>Inspector Signature: _____________________________ Date: _____________</p></div>
  </body></html>`;
};

export default ProductionQueuePage;
